import { useContext, useEffect, useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import throttle from 'lodash-es/throttle';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router';

import { SocketContext } from '@/contexts/rtm';
import useDesktopNotifications from '@/hooks/useDesktopNotifications';
import useNotificationSounds from '@/hooks/useNotificationSounds';
import { useUserRoles } from '@/hooks/useUserRoles';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectSessionId } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import { ChatState } from '@/shared/types/chat';
import { actions } from '@/shared/types/permissions';
import {
  ConversationAssignee,
  ConversationRouting,
  ServerToClientEvents,
  SocketEvent,
} from '@/shared/types/rtm';
import { RootState } from '@/shared/types/state';

import { CountedState, CountScope } from './countDeltas';
import {
  isFullConversation,
  onConversationAccessLost,
  onConversationCreated,
  onConversationExpired,
  onConversationRemoved,
  onConversationUpdated,
  onVisitorActivity,
} from './useConversations';

const isHandover = (conversation: ConversationAssignee, previous?: ConversationAssignee) =>
  previous !== undefined &&
  ((previous.assignee_agent_id ?? null) !== (conversation.assignee_agent_id ?? null) ||
    (previous.assignee_brain_id ?? null) !== (conversation.assignee_brain_id ?? null));

export const useRtmConversations = (deskId: string) => {
  const { t } = useTranslation();
  const { showNotifications } = useDesktopNotifications();
  const { play } = useNotificationSounds();
  const { socket } = useContext(SocketContext);
  const userId = useSelector(selectUserId);
  const slug = useSelector(selectAccountSlug);
  const sessionId = useSelector(selectSessionId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { search } = useLocation();
  // The Redux session id lags the URL, so the open conversation comes from the route.
  const openSessionId = useMatch('/:slug/chats/:deskId/conversations/:sessionId')?.params.sessionId;

  // Routing can send several updates for one handover (round robin and balanced modes).
  const throttledPlay = throttle(
    ({ url, text }) => {
      play();
      showNotifications({ url, text });
    },
    400,
    { leading: false, trailing: true }
  );

  const canReadDepartmentsOthers = useSelector((state: RootState) =>
    getPermissions(state, 'conversations.read.departments', actions.OTHERS)
  );

  const canReadConversationsOthers = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.READ_OTHERS)
  );

  const canReadUnassigned = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.READ_UNASSIGNED)
  );

  const canReadBrain = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.READ_BRAIN)
  );

  const { getUserDepartments } = useUserRoles(userId);

  /* If the user can't read other departments' conversations and the conversation
      belongs to another department, don't update the cache.
      Also if the user can't read other agents' conversations and the conversation belongs to another agent.
      Without read.others, only the open unassigned queue is readable besides the user's own
      conversations, and only with read.unassigned — the same scope as the list endpoint.
      Conversations the AI agent is handling need read.brain.
  */
  const preventConversationUpdate = useCallback(
    (conversation: CountedState) => {
      const userDepartments = getUserDepartments(conversation?.desk_id);

      const conversationHasDepartment = conversation?.department_id;
      const belongsToDepartment = userDepartments?.find(
        (d) => d.department_id === conversation.department_id
      );
      if (
        !canReadConversationsOthers &&
        conversation.assignee_agent_id &&
        conversation.assignee_agent_id !== userId
      ) {
        return true;
      }
      const isBrainOwned = !conversation.assignee_agent_id && !!conversation.assignee_brain_id;
      if (isBrainOwned && !canReadBrain) {
        return true;
      }
      const isInQueue =
        !conversation.assignee_agent_id &&
        !conversation.assignee_brain_id &&
        conversation.status === ChatState.OPEN;
      if (
        !canReadConversationsOthers &&
        !conversation.assignee_agent_id &&
        !(canReadUnassigned && isInQueue) &&
        !isBrainOwned
      ) {
        return true;
      }
      if (
        conversationHasDepartment &&
        !canReadDepartmentsOthers &&
        !belongsToDepartment &&
        conversation.assignee_agent_id !== userId
      ) {
        return true;
      }
      return false;
    },
    [
      canReadConversationsOthers,
      canReadUnassigned,
      canReadBrain,
      canReadDepartmentsOthers,
      getUserDepartments,
      userId,
    ]
  );

  // Stable, because the socket handlers below depend on it.
  const countScope: CountScope = useMemo(
    () => ({
      userId,
      canReadOthers: canReadConversationsOthers,
      canRead: (state) => !preventConversationUpdate(state),
      isReady: canReadDepartmentsOthers || !!getUserDepartments(deskId),
    }),
    [
      canReadConversationsOthers,
      canReadDepartmentsOthers,
      deskId,
      getUserDepartments,
      preventConversationUpdate,
      userId,
    ]
  );

  const closeIfOpen = useCallback(
    (closedSessionId: string) => {
      if (closedSessionId === openSessionId) {
        navigate(`/${slug}/chats/${deskId}/conversations${search}`, { replace: true });
      }
    },
    [deskId, navigate, openSessionId, search, slug]
  );

  const handleAccessLost = useCallback(
    (conversation: ConversationRouting, previous?: ConversationAssignee) => {
      if (conversation.desk_id !== deskId) {
        return;
      }
      closeIfOpen(conversation.session_id);
      onConversationAccessLost({ queryClient, conversation, previous, deskId, countScope });
    },
    [closeIfOpen, countScope, deskId, queryClient]
  );

  const handleCreated: ServerToClientEvents[SocketEvent.conversations_created] = useCallback(
    ({ data: conversation }) => {
      if (!isFullConversation(conversation)) {
        handleAccessLost(conversation);
        return;
      }
      onConversationCreated({
        queryClient,
        conversation,
        deskId,
        canRead: !preventConversationUpdate(conversation),
        countScope,
      });
    },
    [countScope, deskId, handleAccessLost, preventConversationUpdate, queryClient]
  );

  const handleUpdated: ServerToClientEvents[SocketEvent.conversations_updated] = useCallback(
    ({ data: conversation, previous, user_id }) => {
      const handover = isHandover(conversation, previous) && conversation.desk_id === deskId;
      if (!isFullConversation(conversation)) {
        if (handover) {
          throttledPlay.cancel();
        }
        handleAccessLost(conversation, previous);
        return;
      }
      const canRead = !preventConversationUpdate(conversation);
      if (handover) {
        const isAssignedToMe = conversation.assignee_agent_id === userId;
        const isUnassigned = !conversation.assignee_agent_id && !conversation.assignee_brain_id;
        if (canRead && (isAssignedToMe || isUnassigned)) {
          throttledPlay({
            url: `/${slug}/chats/${deskId}/conversations/${conversation.session_id}`,
            text: t('conversation.user_asked_for_agent'),
          });
        } else {
          throttledPlay.cancel();
        }
      }
      if (
        conversation.assignee_agent_id === userId &&
        conversation.session_id !== sessionId &&
        conversation.agent_unread_count > 0 &&
        conversation.status === ChatState.OPEN &&
        canRead
      ) {
        throttledPlay({
          url: `/${slug}/chats/${deskId}/conversations/${conversation.session_id}`,
          text: t('conversation.you_have_a_new_message'),
        });
      }
      onConversationUpdated({
        queryClient,
        conversation,
        deskId,
        canRead,
        previous,
        changedBy: user_id,
        countScope,
      });
    },
    [
      countScope,
      handleAccessLost,
      deskId,
      preventConversationUpdate,
      queryClient,
      sessionId,
      slug,
      t,
      throttledPlay,
      userId,
    ]
  );

  const handleActivity: ServerToClientEvents[SocketEvent.conversations_activity] = useCallback(
    ({ data: activity }) => {
      onVisitorActivity(queryClient, activity, deskId);
    },
    [deskId, queryClient]
  );

  const handleExpired: ServerToClientEvents[SocketEvent.conversations_expired] = useCallback(
    ({ data: conversation, previous }) => {
      if (!isFullConversation(conversation)) {
        handleAccessLost(conversation, previous);
        return;
      }
      onConversationExpired({
        queryClient,
        conversation,
        deskId,
        canRead: !preventConversationUpdate(conversation),
        previous,
        countScope,
      });
    },
    [countScope, handleAccessLost, deskId, preventConversationUpdate, queryClient]
  );

  const handleRemoved: ServerToClientEvents[SocketEvent.conversations_removed] = useCallback(
    ({ data }) => {
      closeIfOpen(data.session_id);
      onConversationRemoved({ queryClient, conversation: data, deskId, countScope });
    },
    [closeIfOpen, countScope, deskId, queryClient]
  );

  useEffect(() => {
    socket?.on(SocketEvent.conversations_updated, handleUpdated);
    socket?.on(SocketEvent.conversations_created, handleCreated);
    socket?.on(SocketEvent.conversations_activity, handleActivity);
    socket?.on(SocketEvent.conversations_expired, handleExpired);
    socket?.on(SocketEvent.conversations_removed, handleRemoved);
    return () => {
      socket?.off(SocketEvent.conversations_created, handleCreated);
      socket?.off(SocketEvent.conversations_updated, handleUpdated);
      socket?.off(SocketEvent.conversations_activity, handleActivity);
      socket?.off(SocketEvent.conversations_expired, handleExpired);
      socket?.off(SocketEvent.conversations_removed, handleRemoved);
    };
  }, [handleCreated, handleUpdated, handleActivity, socket, handleExpired, handleRemoved]);
};
